import React, { useState, useEffect, useCallback, memo } from "react";
import { Table, Button, Typography, Input, message, Modal, Space, Tag, Select, Form } from "antd";
import { PlusOutlined, CopyOutlined, DeleteOutlined, EditOutlined, StopOutlined, ReloadOutlined } from "@ant-design/icons";
import moment from "moment";
import { DATE_FORMAT_DD_MM_YYYY } from "constants/DateConstant";
import {
  getActiveFacilities,
  getAllFacilityTokens,
  generateFacilityToken,
  revokeFacilityToken,
  unpairTablet,
  renameTablet,
} from "./workpulseApi";

const { Text } = Typography;
const { confirm } = Modal;
const { Option } = Select;

const GenerateTokenModal = ({ visible, onClose, onGenerated }) => {
  const [facilities, setFacilities] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      setSelectedId(null);
      getActiveFacilities().then(({ error, result }) => {
        if (!error && Array.isArray(result?.result)) setFacilities(result.result);
      });
    }
  }, [visible]);

  const handleGenerate = async () => {
    if (!selectedId) {
      message.error("Please select a facility first.");
      return;
    }
    setLoading(true);
    const { error, result } = await generateFacilityToken(selectedId);
    setLoading(false);
    if (error) {
      message.error(result?.message || "Failed to generate code.");
      return;
    }
    message.success("Pairing code generated.");
    onGenerated();
    onClose();
  };

  return (
    <Modal title="Generate a Pairing Code" visible={visible} onCancel={onClose} onOk={handleGenerate} confirmLoading={loading} destroyOnClose>
      <Form layout="vertical">
        <Form.Item label="Facility" required>
          <Select showSearch optionFilterProp="children" placeholder="Select a facility" style={{ width: "100%" }} value={selectedId} onChange={setSelectedId}>
            {facilities.map((f) => <Option key={f.facility_id} value={f.facility_id}>{f.facility_name}</Option>)}
          </Select>
        </Form.Item>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Generates a one-time 9-digit code for pairing a single tablet. Generate a new code for each additional tablet at this facility.
        </Text>
      </Form>
    </Modal>
  );
};

const rowStatus = (row) => {
  if (!row.used_by_tablet_id) return "pending";
  if (row.tablet_unpaired) return "unpaired";
  return "paired";
};

const FacilityTablets = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generateVisible, setGenerateVisible] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { error, result } = await getAllFacilityTokens();
    setRows(!error && Array.isArray(result?.result) ? result.result : []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCopy = useCallback((code) => {
    navigator.clipboard?.writeText(code);
    message.success("Code copied to clipboard.");
  }, []);

  const handleRevoke = useCallback((row) => {
    confirm({
      title: "Revoke pairing code",
      content: `This code for ${row.facility_name} hasn't been used yet. Revoking it means it can no longer pair a tablet.`,
      okText: "Revoke",
      okType: "danger",
      onOk: async () => {
        const { error, result } = await revokeFacilityToken(row.id);
        if (error) message.error(result?.message || "Failed to revoke code.");
        else { message.success("Code revoked."); load(); }
      },
    });
  }, [load]);

  const handleRegenerate = useCallback(async (row) => {
    const { error: revokeError, result: revokeResult } = await revokeFacilityToken(row.id);
    if (revokeError) { message.error(revokeResult?.message || "Failed to regenerate code."); return; }
    const { error: genError, result: genResult } = await generateFacilityToken(row.facility_id);
    if (genError) { message.error(genResult?.message || "Failed to regenerate code."); return; }
    message.success("Code regenerated.");
    load();
  }, [load]);

  const handleUnpair = useCallback((row) => {
    confirm({
      title: "Unpair tablet",
      content: `Unpair "${row.device_label || "this tablet"}" from ${row.facility_name}? It will stop being able to log activity. The pairing code stays on record but can't be reused.`,
      okText: "Unpair",
      okType: "danger",
      onOk: async () => {
        const { error, result } = await unpairTablet(row.used_by_tablet_id);
        if (error) message.error(result?.message || "Failed to unpair tablet.");
        else { message.success("Tablet unpaired."); load(); }
      },
    });
  }, [load]);

  const handleRename = useCallback((row) => {
    let newLabel = row.device_label || "";
    Modal.confirm({
      title: "Rename tablet",
      icon: <EditOutlined />,
      content: <Input defaultValue={row.device_label} placeholder="Tablet label" onChange={(e) => { newLabel = e.target.value; }} />,
      okText: "Save",
      onOk: async () => {
        const { error, result } = await renameTablet(row.used_by_tablet_id, newLabel);
        if (error) message.error(result?.message || "Failed to rename tablet.");
        else { message.success("Tablet renamed."); load(); }
      },
    });
  }, [load]);

  const columns = [
    { title: "Facility", dataIndex: "facility_name", key: "facility_name" },
    {
      title: "Code",
      dataIndex: "pairing_token",
      key: "pairing_token",
      render: (v) => <Tag style={{ fontFamily: "monospace", fontSize: 14, padding: "3px 10px" }}>{v}</Tag>,
    },
    {
      title: "Status",
      key: "status",
      render: (_, row) => {
        const status = rowStatus(row);
        if (status === "pending") return <Tag color="orange">Pending</Tag>;
        if (status === "unpaired") return <Tag>Unpaired</Tag>;
        return <Tag color="green">Paired</Tag>;
      },
    },
    {
      title: "Device",
      dataIndex: "device_label",
      key: "device_label",
      render: (v, row) => (row.used_by_tablet_id ? (v || <Text type="secondary">Unlabelled</Text>) : "-"),
    },
    {
      title: "Date",
      key: "date",
      render: (_, row) => {
        const v = row.used_date || row.generated_date;
        return v ? moment(v).format(DATE_FORMAT_DD_MM_YYYY) : "-";
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, row) => {
        const status = rowStatus(row);
        if (status === "pending") {
          return (
            <Space>
              <Button size="small" icon={<CopyOutlined />} onClick={() => handleCopy(row.pairing_token)}>Copy</Button>
              <Button size="small" icon={<ReloadOutlined />} onClick={() => handleRegenerate(row)}>Regenerate</Button>
              <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleRevoke(row)}>Revoke</Button>
            </Space>
          );
        }
        if (status === "paired") {
          return (
            <Space>
              <Button size="small" icon={<EditOutlined />} onClick={() => handleRename(row)}>Rename</Button>
              <Button size="small" danger icon={<StopOutlined />} onClick={() => handleUnpair(row)}>Unpair</Button>
            </Space>
          );
        }
        return <Text type="secondary" style={{ fontSize: 12 }}>No actions available</Text>;
      },
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setGenerateVisible(true)}>
          Generate Pairing Code
        </Button>
      </div>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={rows}
        loading={loading}
        size="middle"
        pagination={{ pageSize: 20 }}
        locale={{ emptyText: "No pairing codes yet — click Generate Pairing Code to set up a tablet." }}
      />
      <GenerateTokenModal
        visible={generateVisible}
        onClose={() => setGenerateVisible(false)}
        onGenerated={load}
      />
    </div>
  );
};

export default memo(FacilityTablets);
